import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Check, Zap, Tag, CheckCircle, XCircle, User, LogOut, Info, Star, Calendar, Clock, Gift, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Timer constants
const PROMO_CODE = 'BIENVENUE20';
const TIMER_DURATION = 48 * 60 * 60 * 1000; // 48h
const STORAGE_KEY = 'boost_promo_timer_start';

// URLs des captures d'écran des témoignages LinkedIn
const TESTIMONIAL_SCREENSHOTS = {
  michelle: 'https://customer-assets.emergentagent.com/job_f8bb1ad7-5164-4c11-b0a5-dc84a9210d0e/artifacts/09pq2mrt_michelle-partie1.png',
  sylvie: 'https://customer-assets.emergentagent.com/job_f8bb1ad7-5164-4c11-b0a5-dc84a9210d0e/artifacts/ccrd76cd_sylvie.png',
  alexis: 'https://customer-assets.emergentagent.com/job_f8bb1ad7-5164-4c11-b0a5-dc84a9210d0e/artifacts/7ow8looh_alexis.png',
  valerie: 'https://customer-assets.emergentagent.com/job_f8bb1ad7-5164-4c11-b0a5-dc84a9210d0e/artifacts/qx2p4yz0_valerie.png'
};

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [promoTimeLeft, setPromoTimeLeft] = useState(null);
  const [isPromoExpired, setIsPromoExpired] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' ou 'annual'
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll en haut de la page au chargement
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Timer promo code
  useEffect(() => {
    const startTime = localStorage.getItem(STORAGE_KEY);
    
    if (!startTime) {
      setIsPromoExpired(true);
      return;
    }

    const updateTimer = () => {
      const elapsed = Date.now() - parseInt(startTime);
      const remaining = TIMER_DURATION - elapsed;

      if (remaining <= 0) {
        setIsPromoExpired(true);
        setPromoTimeLeft(null);
      } else {
        setIsPromoExpired(false);
        setPromoTimeLeft(remaining);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format time helper
  const formatTime = (ms) => {
    if (!ms) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Vérifier si l'utilisateur est connecté
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Pré-remplir le code promo si venant de la Thank You page ou après connexion
  useEffect(() => {
    // Cas 1: Venant de Thank You page
    if (location.state?.fromThankYou && location.state?.promoCode && !isPromoExpired) {
      setPromoCode(location.state.promoCode);
    }
    // Cas 2: Retour après connexion avec code promo et plan sélectionné
    else if (location.state?.promoCode) {
      setPromoCode(location.state.promoCode);
      // Si un plan était sélectionné avant la connexion, le re-sélectionner et ouvrir le modal
      if (location.state?.selectedPlan) {
        setSelectedPlan(location.state.selectedPlan);
        setShowPromoInput(true);
        // Valider le code promo automatiquement
        setTimeout(() => validatePromoCode(location.state.promoCode, location.state.selectedPlan), 500);
      }
    }
  }, [location.state, isPromoExpired]);

  // Scroll vers l'ancre #offres si présente dans l'URL
  useEffect(() => {
    if (location.hash === '#offres') {
      setTimeout(() => {
        const element = document.getElementById('offres');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    navigate('/');
  };

  const handleSelectPlan = async (plan, isTrial = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Passer le code promo et le plan sélectionné pour les récupérer après connexion
      navigate('/auth', { state: { returnTo: '/pricing', promoCode: promoCode, selectedPlan: plan, isTrial } });
      return;
    }

    if (plan === 'free') {
      navigate('/onboarding');
      return;
    }

    // Si c'est un essai gratuit, aller directement au checkout sans promo
    if (isTrial) {
      await startTrialCheckout(plan);
      return;
    }

    setSelectedPlan(plan);
    setShowPromoInput(true);
    setPromoValidation(null);
    // Si le code promo est déjà pré-rempli, le valider automatiquement avec le plan sélectionné
    if (promoCode) {
      setTimeout(() => validatePromoCode(promoCode, plan), 300);
    }
  };

  const startTrialCheckout = async (plan) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/payments/checkout/session`, {
        plan,
        origin_url: window.location.origin,
        is_trial: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Trial checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'essai');
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async (code, planOverride = null) => {
    if (!code || code.length < 3) {
      setPromoValidation(null);
      return;
    }
    
    setValidatingPromo(true);
    try {
      const token = localStorage.getItem('token');
      const planType = planOverride?.split('_')[0] || selectedPlan?.split('_')[0] || 'pro';
      
      // D'abord essayer la validation admin qui supporte tous les types de codes
      const response = await axios.get(`${API}/admin/promo-codes/validate/${code}?plan=${planType}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Si c'est un code no_cb, afficher un message spécial
      if (response.data.valid && response.data.discount_type === 'no_cb') {
        setPromoValidation({
          ...response.data,
          is_no_cb: true,
          message: response.data.discount_text
        });
      } else {
        setPromoValidation(response.data);
      }
    } catch (error) {
      // Fallback sur l'endpoint payments si admin échoue
      try {
        const token = localStorage.getItem('token');
        const planType = planOverride?.split('_')[0] || selectedPlan?.split('_')[0] || 'pro';
        const fallbackResponse = await axios.get(`${API}/payments/validate-promo/${code}?plan=${planType}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setPromoValidation(fallbackResponse.data);
      } catch (fallbackError) {
        if (fallbackError.response?.status === 401) {
          setPromoValidation({ valid: true, message: 'Code sera appliqué au paiement', discount_value: 20, discount_type: 'percent' });
        } else {
          setPromoValidation({ valid: false, message: 'Code promo invalide, expiré ou non applicable à ce plan' });
        }
      }
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePromoCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setPromoCode(code);
    // Debounce validation
    clearTimeout(window.promoValidationTimeout);
    window.promoValidationTimeout = setTimeout(() => validatePromoCode(code), 500);
  };

  const proceedToCheckout = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      // Si c'est un code no_cb, appliquer directement le code promo à l'utilisateur
      if (promoValidation?.is_no_cb && promoValidation?.valid) {
        const response = await axios.post(
          `${API}/auth/apply-promo-code`,
          { promo_code: promoCode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(response.data.message);
        setShowPromoInput(false);
        
        // Rediriger vers le dashboard après un court délai
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
        return;
      }

      // Sinon, procéder au checkout Stripe normal
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          plan: selectedPlan,
          promo_code: promoCode || null,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la session de paiement');
    } finally {
      setLoading(false);
    }
  };


  const plans = [
    {
      name: 'Professionnel',
      price: '79€',
      period: '/mois',
      description: 'Pour entrepreneurs et consultants',
      features: [
        '2 études complètes par mois',
        'Accès illimité aux 50 étapes',
        '2 coachings live groupés par mois (valeur 120 €)',
        'Support prioritaire',
        'Export PDF de vos stratégies',
        'Possibilité d\'acheter des études supplémentaires'
      ],
      cta: 'Essayer 7 jours gratuits',
      plan: 'pro_monthly',
      highlighted: true,
      hasTrial: true,
      annual: {
        price: '790€',
        period: '/an',
        save: '2 mois offerts',
        plan: 'pro_annual'
      }
    },
    {
      name: 'Agence',
      price: '199€',
      period: '/mois',
      description: 'Pour agences et équipes marketing',
      features: [
        '5 études complètes par mois',
        'Accès illimité aux 50 étapes',
        '2 coachings live groupés par mois (valeur 120 €)',
        'Support prioritaire',
        'Export PDF de vos stratégies',
        'Accès multi-utilisateurs',
        'Études supplémentaires à tarif préférentiel'
      ],
      cta: 'Essayer 7 jours gratuits',
      plan: 'agency_monthly',
      highlighted: false,
      hasTrial: true,
      annual: {
        price: '1990€',
        period: '/an',
        save: '2 mois offerts',
        plan: 'agency_annual'
      }
    },
    {
      name: 'Done For You',
      price: 'Sur mesure',
      period: '',
      description: 'Valérie fait tout pour vous',
      features: [
        { text: 'Audit stratégique complet de votre business', highlight: true },
        'Stratégie marketing personnalisée',
        'Création de vos offres et positionnement',
        'Plan d\'action détaillé et priorisé'
      ],
      cta: 'Prendre rendez-vous',
      plan: 'done_for_you',
      highlighted: false,
      isCalendly: true,
      calendlyUrl: 'https://calendly.com/valerie-madej/boost-strategia-audit'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-heading font-bold">Boost-StrategIA</span>
          </Link>
          
          {isLoggedIn ? (
            <nav className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="dashboard-link">
                <Button variant="ghost" size="sm">
                  <User className="w-4 h-4 mr-2" />
                  Mon compte
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </nav>
          ) : (
            <Link to="/auth" data-testid="login-link">
              <Button variant="outline">Connexion</Button>
            </Link>
          )}
        </div>
      </header>

      {/* Promo Banner */}
      {!isPromoExpired && promoTimeLeft && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-[72px] left-0 right-0 z-40 bg-gradient-to-r from-primary/20 via-pink-500/20 to-primary/20 border-b border-primary/30"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3 flex-wrap">
            <Gift className="w-5 h-5 text-primary" />
            <span className="text-sm sm:text-base">
              Le code de réduction <span className="font-bold text-primary">{PROMO_CODE}</span> (-20%) prend fin dans
            </span>
            <span className="font-mono font-bold text-primary text-lg bg-background/50 px-3 py-1 rounded">
              {formatTime(promoTimeLeft)}
            </span>
          </div>
        </motion.div>
      )}

      {/* Pricing Section */}
      <section className={`${!isPromoExpired && promoTimeLeft ? 'pt-44' : 'pt-32'} pb-20 px-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-4" data-testid="pricing-title">
                Tarifs simples et transparents
              </h1>
              <p className="text-lg text-muted mb-8" data-testid="pricing-description">
                Choisissez l'offre qui correspond à vos besoins
              </p>
              
              {/* Toggle Mensuel / Annuel */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    billingCycle === 'monthly' 
                      ? 'bg-primary text-black' 
                      : 'bg-card border border-border text-muted hover:border-primary/50'
                  }`}
                  data-testid="billing-monthly"
                >
                  Mensuel
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-full font-medium transition-all flex items-center gap-2 ${
                    billingCycle === 'annual' 
                      ? 'bg-primary text-black' 
                      : 'bg-card border border-border text-muted hover:border-primary/50'
                  }`}
                  data-testid="billing-annual"
                >
                  Annuel
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    billingCycle === 'annual' 
                      ? 'bg-black/20 text-black' 
                      : 'bg-pink-500/20 text-pink-400'
                  }`}>
                    -17%
                  </span>
                </button>
              </div>
            </motion.div>
          </div>

          <div id="offres" className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, i) => {
              // Déterminer le prix et le plan selon le cycle
              const isAnnual = billingCycle === 'annual';
              const displayPrice = isAnnual && plan.annual ? plan.annual.price : plan.price;
              const displayPeriod = isAnnual && plan.annual ? plan.annual.period : plan.period;
              const activePlan = isAnnual && plan.annual ? plan.annual.plan : plan.plan;
              
              return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`bg-card border rounded-lg p-8 relative ${
                  plan.highlighted ? 'border-primary shadow-glow-md' : 'border-border'
                }`}
                data-testid={`pricing-card-${plan.plan}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2" data-testid="popular-badge">
                    <span className="bg-primary text-black px-4 py-1 rounded-full text-sm font-semibold">
                      Plus populaire
                    </span>
                  </div>
                )}
                
                {/* Badge essai gratuit */}
                {plan.hasTrial && !isAnnual && (
                  <div className="absolute -top-4 right-4">
                    <span className="bg-gradient-to-r from-primary to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      7 jours gratuits
                    </span>
                  </div>
                )}
                
                {/* Badge économie pour annuel */}
                {isAnnual && plan.annual && (
                  <div className="absolute -top-4 right-4">
                    <span className="bg-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {plan.annual.save}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-heading font-semibold mb-2" data-testid={`plan-name-${plan.plan}`}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold" data-testid={`plan-price-${plan.plan}`}>{displayPrice}</span>
                    <span className="text-muted" data-testid={`plan-period-${plan.plan}`}>{displayPeriod}</span>
                  </div>
                  {/* Afficher l'équivalent mensuel pour l'annuel */}
                  {isAnnual && plan.annual && (
                    <p className="text-sm text-primary">
                      soit {plan.price.replace('€', '')}€ x 10 mois
                    </p>
                  )}
                  <p className="text-muted text-sm mt-2" data-testid={`plan-description-${plan.plan}`}>{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => {
                    const featureText = typeof feature === 'object' ? feature.text : feature;
                    const isHighlighted = typeof feature === 'object' && feature.highlight;
                    const isCoaching = typeof feature === 'string' && feature.includes('coachings live');
                    return (
                      <li key={j} className="flex items-start gap-3" data-testid={`plan-feature-${plan.plan}-${j}`}>
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isHighlighted ? 'text-pink-500' : isCoaching ? 'text-accent' : 'text-primary'}`} />
                        <span className={`text-sm ${isHighlighted ? 'text-pink-500 font-semibold' : isCoaching ? 'text-accent font-semibold' : ''}`}>{featureText}</span>
                      </li>
                    );
                  })}
                </ul>

                {plan.isCalendly ? (
                  <a 
                    href={plan.calendlyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button
                      className="w-full bg-blue-500 hover:bg-blue-600"
                      variant="default"
                      data-testid={`plan-cta-${plan.plan}`}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {plan.cta}
                    </Button>
                  </a>
                ) : plan.hasTrial ? (
                  <div className="space-y-3">
                    <Button
                      className={`w-full ${
                        plan.highlighted ? 'shadow-glow-md bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-600' : ''
                      }`}
                      variant={plan.highlighted ? 'default' : 'outline'}
                      onClick={() => handleSelectPlan(activePlan, true)}
                      disabled={loading}
                      data-testid={`plan-cta-${plan.plan}`}
                    >
                      {loading ? 'Chargement...' : plan.cta}
                    </Button>
                    <Button
                      className="w-full bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30 hover:text-blue-300"
                      variant="outline"
                      onClick={() => handleSelectPlan(activePlan, false)}
                      disabled={loading}
                      data-testid={`plan-direct-${plan.plan}`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Passer à la version payante
                    </Button>
                  </div>
                ) : (
                  <Button
                    className={`w-full ${
                      plan.highlighted ? 'shadow-glow-md' : ''
                    }`}
                    variant={plan.highlighted ? 'default' : 'outline'}
                    onClick={() => handleSelectPlan(activePlan)}
                    disabled={loading}
                    data-testid={`plan-cta-${plan.plan}`}
                  >
                    {plan.cta}
                  </Button>
                )}
              </motion.div>
            )})}
          </div>

          {/* Code promo modal */}
          {showPromoInput && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6"
              onClick={() => setShowPromoInput(false)}
            >
              <div 
                className="bg-card border border-border rounded-lg p-8 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
                data-testid="promo-modal"
              >
                <h3 className="text-2xl font-heading font-semibold mb-4">Code de réduction</h3>
                <p className="text-muted mb-6">Avez-vous un code promo ?</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted block mb-2">Code promo (optionnel)</label>
                    <div className="relative">
                      <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
                      <Input
                        value={promoCode}
                        onChange={handlePromoCodeChange}
                        placeholder="BIENVENUE20"
                        className="pl-10"
                        data-testid="promo-input"
                      />
                      {validatingPromo && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Promo validation result */}
                  {promoValidation && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg flex items-center gap-2 ${
                        promoValidation.valid 
                          ? promoValidation.is_no_cb 
                            ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400'
                            : 'bg-green-500/10 border border-green-500/30 text-green-400'
                          : 'bg-red-500/10 border border-red-500/30 text-red-400'
                      }`}
                    >
                      {promoValidation.valid ? (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">
                            {promoValidation.is_no_cb 
                              ? `✨ ${promoValidation.discount_text}`
                              : promoValidation.discount_text || `-${promoValidation.discount_value}% de réduction appliquée !`
                            }
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5" />
                          <span>{promoValidation.message}</span>
                        </>
                      )}
                    </motion.div>
                  )}

                  {/* Boutons du modal promo */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowPromoInput(false)}
                      className="flex-1"
                      data-testid="promo-cancel"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={proceedToCheckout}
                      disabled={loading || (promoValidation && !promoValidation.valid)}
                      className={`flex-1 ${promoValidation?.is_no_cb ? 'bg-blue-500 hover:bg-blue-600' : ''} shadow-glow-md`}
                      data-testid="promo-confirm"
                    >
                      {loading ? 'Chargement...' : promoValidation?.is_no_cb ? 'Activer mon accès gratuit' : 'Continuer'}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Solidarité animale */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-xl p-6 max-w-2xl mx-auto flex flex-col md:flex-row items-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_65659477-73eb-427c-9057-d93b7f82d27e/artifacts/ytwjdp0n_slide9_mascottes.png"
                alt="Nos mascottes pour la protection animale"
                className="w-32 h-auto"
              />
              <div className="text-left">
                <p className="text-lg font-semibold text-pink-400 mb-2">
                  🐾 Ensemble pour la cause animale
                </p>
                <p className="text-muted text-sm">
                  <span className="text-white font-medium">15% de nos revenus</span> sont reversés à des associations de protection animale. 
                  En choisissant Boost-StrategIA, vous contribuez à protéger nos amis à quatre pattes.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Extra Studies */}
          <div className="mt-12 text-center">
            <div className="bg-card border border-border rounded-lg p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-heading font-semibold mb-4" data-testid="extra-studies-title">
                Études supplémentaires
              </h3>
              <p className="text-muted mb-6" data-testid="extra-studies-description">
                Besoin de plus d'études ? Achetez des études à la carte pour seulement <span className="text-primary font-semibold">29,99€</span> l'unité.
              </p>
              <p className="text-sm text-muted" data-testid="extra-studies-note">
                Disponible une fois connecté à votre compte
              </p>
            </div>
          </div>

          {/* Testimonials Section on Pricing */}
          <div className="mt-20" data-testid="pricing-testimonials">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-4xl font-heading font-semibold mb-3">
                Ils ont boosté leur marketing
              </h2>
              <p className="text-muted mb-4">
                Rejoignez des entrepreneurs qui ont transformé leur stratégie
              </p>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2 text-sm">
                <Info className="w-4 h-4 text-primary" />
                <span className="text-muted">
                  <span className="text-primary font-medium">Business Boosteur</span> est l'ancienne version de Boost-StrategIA
                </span>
              </div>
            </div>

            {/* Audio Testimonials - empilés verticalement */}
            <div className="max-w-2xl mx-auto mb-8 space-y-4">
              {/* Michelle - Audio */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
                className="bg-card border border-primary/30 rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-primary fill-primary" />
                  <span className="text-xs font-medium text-primary">Témoignage audio - Michelle DEPRE</span>
                </div>
                <div className="aspect-video bg-black/50 rounded-lg overflow-hidden relative">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/GAZyfjwZp7w" 
                    title="Témoignage audio Michelle DEPRE"
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    className="absolute inset-0"
                  ></iframe>
                </div>
              </motion.div>

              {/* Sylvie - Audio */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-accent/30 rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-accent fill-accent" />
                  <span className="text-xs font-medium text-accent">Témoignage audio - Sylvie Stevanovic</span>
                </div>
                <div className="aspect-video bg-black/50 rounded-lg overflow-hidden relative">
                  <iframe 
                    width="100%" 
                    height="100%" 
                    src="https://www.youtube.com/embed/pYYms2c26RQ" 
                    title="Témoignage audio Sylvie Stevanovic"
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    className="absolute inset-0"
                  ></iframe>
                </div>
              </motion.div>
            </div>

            {/* Screenshot Testimonials */}
            <div className="grid grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                { key: 'michelle', name: 'Michelle DEPRE' },
                { key: 'sylvie', name: 'Sylvie Stevanovic' },
                { key: 'alexis', name: 'Alexis Rocco' },
                { key: 'valerie', name: 'Valérie Beaufay' }
              ].map((testimonial, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                >
                  <img 
                    src={TESTIMONIAL_SCREENSHOTS[testimonial.key]}
                    alt={`Témoignage LinkedIn de ${testimonial.name}`}
                    className="w-full h-auto"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-card/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-pink-500/20 border border-primary/30 rounded-full px-4 py-2 mb-4"
            >
              <span className="flex items-center justify-center w-6 h-6 bg-primary text-black text-sm font-bold rounded-full">5</span>
              <span className="text-sm font-medium text-primary">questions essentielles avant de vous lancer</span>
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Questions fréquentes
            </h2>
            <p className="text-muted">Tout ce que vous devez savoir avant de commencer</p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "Est-ce que Boost-StrategIA remplace un consultant ?",
                a: "Non — il te donne accès à la même méthodologie que les meilleurs consultants marketing, pour 1% du prix. Idéal si tu n'as pas le budget pour un accompagnement haut de gamme."
              },
              {
                q: "En combien de temps j'obtiens ma stratégie ?",
                a: "Ta première étude complète est prête en quelques heures. Tu avances à ton rythme, étape par étape, sans pression."
              },
              {
                q: "Et si je ne vois pas de résultats ?",
                a: "La méthode a généré jusqu'à +120% de CA pour nos clients sur un seul lancement. Si tu l'appliques sérieusement, les résultats suivent. Et sinon tu as les coachings groupés en live 2 fois/mois avec Valérie Madej pour poser tes questions et obtenir de précieux conseils."
              },
              {
                q: "C'est fait pour moi si je ne suis pas très à l'aise avec les outils digitaux ?",
                a: <>Boost-StrategIA est fait pour être simple. Si tu sais taper dans un formulaire, tu sais utiliser l'outil. Sinon, il y a une formule en Done for You, où Valérie fait tout pour toi. Contacte-la par mail à <a href="mailto:valerie.madej@gmail.com" className="text-primary hover:underline">valerie.madej@gmail.com</a> pour en faire la demande.</>
              },
              {
                q: "Je peux annuler à tout moment ?",
                a: "Oui, sans engagement et sans pénalité. Tu restes parce que ça fonctionne, parce que tu bénéficies d'un accompagnement en coachings groupés live 2 fois/mois et pas parce que tu es coincée."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/30 transition-colors"
              >
                <h3 className="font-semibold text-lg mb-3 flex items-start gap-3">
                  <span className="text-primary">Q:</span>
                  {faq.q}
                </h3>
                <p className="text-muted pl-7">
                  <span className="text-pink-400 font-medium">R:</span> {faq.a}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-muted">
          <p>© 2025 Boost-StrategIA. Tous droits réservés.</p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-primary transition-colors">
              Politique de confidentialité
            </Link>
            <span className="text-border">|</span>
            <Link to="/terms" className="hover:text-primary transition-colors">
              CGV
            </Link>
            <span className="text-border">|</span>
            <a href="mailto:contact@boost-strategia.com" className="hover:text-primary transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;